"use client";

import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState, type ReactNode } from "react";
import { cn } from "@ui/lib";

interface CollapsibleProps {
	children: ReactNode;
	title?: string;
	summary?: string;
	defaultOpen?: boolean;
	showLines?: number;
	className?: string;
}

export function Collapsible({
	children,
	title = "Show more",
	summary,
	defaultOpen = false,
	showLines,
	className,
}: CollapsibleProps) {
	const [isOpen, setIsOpen] = useState(defaultOpen);

	// If showLines is specified, we need to handle partial content display
	if (showLines && typeof children === "string") {
		const lines = children.split("\n");
		const visibleLines = lines.slice(0, showLines);
		const hiddenLines = lines.slice(showLines);

		return (
			<div className={cn("not-prose", className)}>
				<div className="whitespace-pre-wrap font-mono text-sm">
					{visibleLines.join("\n")}
				</div>
				{hiddenLines.length > 0 && (
					<>
						{isOpen && (
							<div className="whitespace-pre-wrap font-mono text-sm">
								{hiddenLines.join("\n")}
							</div>
						)}
						<button
							type="button"
							onClick={() => setIsOpen(!isOpen)}
							className="mt-2 inline-flex items-center gap-2 rounded-md bg-secondary px-3 py-1.5 text-sm font-medium text-secondary-foreground hover:bg-secondary/80 transition-colors"
						>
							{isOpen ? (
								<>
									<ChevronDown className="h-4 w-4" />
									Show less
								</>
							) : (
								<>
									<ChevronRight className="h-4 w-4" />
									{title} ({hiddenLines.length} more lines)
								</>
							)}
						</button>
					</>
				)}
			</div>
		);
	}

	// Standard collapsible section
	return (
		<div className={cn("not-prose", className)}>
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className="flex w-full items-center justify-between rounded-lg border border-border bg-card p-4 text-left hover:bg-accent/50 transition-colors"
			>
				<div className="flex-1">
					<div className="font-medium text-card-foreground">{title}</div>
					{summary && (
						<div className="mt-1 text-sm text-muted-foreground">{summary}</div>
					)}
				</div>
				{isOpen ? (
					<ChevronDown className="h-5 w-5 text-muted-foreground" />
				) : (
					<ChevronRight className="h-5 w-5 text-muted-foreground" />
				)}
			</button>
			{isOpen && (
				<div className="mt-2 rounded-lg border border-border bg-card p-4">
					<div className="prose dark:prose-invert max-w-none prose-p:text-card-foreground prose-headings:text-card-foreground prose-strong:text-card-foreground prose-code:text-card-foreground">
						{children}
					</div>
				</div>
			)}
		</div>
	);
}

// Alternative component for inline collapsible text with "read more" functionality
interface ReadMoreProps {
	children: ReactNode;
	maxLength?: number;
	showLines?: number;
	readMoreText?: string;
	readLessText?: string;
	className?: string;
}

export function ReadMore({
	children,
	maxLength = 300,
	showLines,
	readMoreText = "Read more",
	readLessText = "Read less",
	className,
}: ReadMoreProps) {
	const [isExpanded, setIsExpanded] = useState(false);

	// Convert children to string for processing, handling React elements properly
	const extractTextContent = (node: ReactNode): string => {
		if (typeof node === "string") return node;
		if (typeof node === "number") return String(node);
		if (Array.isArray(node)) return node.map(extractTextContent).join("");
		if (React.isValidElement(node)) {
			return extractTextContent((node.props as any).children);
		}
		return "";
	};

	const childrenText = extractTextContent(children).trim();

	// Handle line-based truncation
	if (showLines) {
		const lines = childrenText.split("\n").filter(line => line.trim() !== "");
		
		if (lines.length <= showLines) {
			return <div className={className}>{children}</div>;
		}

		const visibleLines = lines.slice(0, showLines);
		const hiddenLines = lines.slice(showLines);

		return (
			<div className={className}>
				<div className="whitespace-pre-wrap">
					{visibleLines.join("\n")}
					{!isExpanded && "..."}
				</div>
				{isExpanded && (
					<div className="whitespace-pre-wrap">{hiddenLines.join("\n")}</div>
				)}
				<button
					type="button"
					onClick={() => setIsExpanded(!isExpanded)}
					className="mt-2 text-sm text-primary hover:text-primary/80 underline underline-offset-4"
				>
					{isExpanded ? readLessText : `${readMoreText} (${hiddenLines.length} more lines)`}
				</button>
			</div>
		);
	}

	// Handle character-based truncation
	if (childrenText.length <= maxLength) {
		return <div className={className}>{children}</div>;
	}

	const truncatedText = childrenText.slice(0, maxLength);
	const remainingText = childrenText.slice(maxLength);

	return (
		<div className={className}>
			<span>
				{truncatedText}
				{!isExpanded && "..."}
			</span>
			{isExpanded && <span>{remainingText}</span>}
			<button
				type="button"
				onClick={() => setIsExpanded(!isExpanded)}
				className="ml-2 text-sm text-primary hover:text-primary/80 underline underline-offset-4"
			>
				{isExpanded ? readLessText : readMoreText}
			</button>
		</div>
	);
}
